import { goKeys as k } from "@/i18n/go-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import {
  UterusOrganIcon,
  MetabolismOrganIcon,
  ThyroidOrganIcon,
} from "@/components/ui/organ-icons"
import { VenusAndMarsIcon } from "@/components/ui/icons"

/* Fill-glyph organ icons get a hairline same-colour stroke to thicken them;
   stroke-based icons (VenusAndMars) keep their own width, so the override is
   per-card, not baked into the card template. */
const GLYPH = "[&_path]:[stroke:currentColor] [&_path]:[stroke-width:0.4]"

/* ── section data (prose lives in the `go` namespace; only the illustrative
   numbers — marker values, units, zone widths — stay inline, per the immunity
   rev-3 pattern). ── */

export const SIGNS = [
  {
    titleKey: k.femaleHormonesSign1Title,
    descKey: k.femaleHormonesSign1Desc,
    glyph: "menstrual" as const,
  },
  {
    titleKey: k.femaleHormonesSign2Title,
    descKey: k.femaleHormonesSign2Desc,
    glyph: "pregnant" as const,
  },
  {
    titleKey: k.femaleHormonesSign3Title,
    descKey: k.femaleHormonesSign3Desc,
    glyph: "skin" as const,
  },
  {
    titleKey: k.femaleHormonesSign4Title,
    descKey: k.femaleHormonesSign4Desc,
    glyph: "weight" as const,
  },
  {
    titleKey: k.femaleHormonesSign5Title,
    descKey: k.femaleHormonesSign5Desc,
    glyph: "moon" as const,
  },
  {
    titleKey: k.femaleHormonesSign6Title,
    descKey: k.femaleHormonesSign6Desc,
    glyph: "bolt" as const,
  },
] as const

/**
 * How-it-works steps. `imaging: true` marks a step that only exists in markets
 * where the imaging arm is bookable — the section drops it and renumbers the
 * rest, so a blood-only market never advertises a scan it can't sell.
 */
export const PROCESS = [
  {
    titleKey: k.femaleHormonesProcessStep1Title,
    descKey: k.femaleHormonesProcessStep1Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
  },
  {
    titleKey: k.femaleHormonesProcessStep2Title,
    descKey: k.femaleHormonesProcessStep2Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
  },
  {
    // Optional MRI visit — the upsell arm, sequenced before the report so the
    // full-picture path reads as one flow. Uses the MRI-walkthrough "scan"
    // photo (distinct from the §7b immersive's mri-suite background, which
    // reused the same asset and read as a duplicate).
    imaging: true,
    titleKey: k.femaleHormonesProcessStep3Title,
    descKey: k.femaleHormonesProcessStep3Desc,
    image: blobMediaUrl("/images/mri/walkthrough/step-scan.png"),
  },
  {
    titleKey: k.femaleHormonesProcessStep4Title,
    descKey: k.femaleHormonesProcessStep4Desc,
    image: blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
  },
] as const

export const STATS = [
  {
    valueKey: k.femaleHormonesStat1Value,
    labelKey: k.femaleHormonesStat1Label,
  },
  {
    valueKey: k.femaleHormonesStat2Value,
    labelKey: k.femaleHormonesStat2Label,
  },
  {
    valueKey: k.femaleHormonesStat3Value,
    labelKey: k.femaleHormonesStat3Label,
  },
  {
    valueKey: k.femaleHormonesStat4Value,
    labelKey: k.femaleHormonesStat4Label,
  },
] as const

export const FAQS = [
  { qKey: k.femaleHormonesFaqQ1, aKey: k.femaleHormonesFaqA1 },
  { qKey: k.femaleHormonesFaqQ2, aKey: k.femaleHormonesFaqA2 },
  { qKey: k.femaleHormonesFaqQ3, aKey: k.femaleHormonesFaqA3 },
  { qKey: k.femaleHormonesFaqQ4, aKey: k.femaleHormonesFaqA4 },
  { qKey: k.femaleHormonesFaqQ5, aKey: k.femaleHormonesFaqA5 },
  { qKey: k.femaleHormonesFaqQ6, aKey: k.femaleHormonesFaqA6 },
] as const

/* ── "What we measure" — the single measure block, in the shared metabolism/
   immunity "health-risks card" design (photo tile + organ icon · title ·
   description · count eyebrow · marker chips). Four groups drawn from different
   panels: three add-on hormone groups (the eight Hormones (women) markers the
   CMS tags `addon-hormones-f`, reusing the existing MeasureG1–G3 copy) plus the
   thyroid + metabolic markers already in the 80-marker Advanced Blood Test.
   Free testosterone and the free androgen index are men's-panel markers and are
   NOT listed here — see docs referenced in the CMS-parity guard. Tiles reuse the abstract
   metabolism gradient textures; markers live in one "·"-joined `markersKey`,
   split into chips at render. */
export const MEASURE_CARDS = [
  {
    // Ordered as a signalling cascade per medical review (Carolina, AHD-7253):
    // the brain's pituitary signals lead, then the ovaries' response, then
    // androgens and the thyroid/metabolic markers.
    titleKey: k.femaleHormonesMeasureG2Title,
    countKey: k.femaleHormonesMeasureG2Count,
    markersKey: k.femaleHormonesMeasureG2Markers,
    bodyKey: k.femaleHormonesMeasureG2Body,
    // LH/FSH are the sex-hormone signals — the DS sex-hormone glyph reads far
    // clearer here than the pituitary/brain organ icon did.
    Icon: VenusAndMarsIcon,
    iconClassName: "h-14 w-14",
    tileImage: "/measure-tile-blood-sugar.png",
  },
  {
    titleKey: k.femaleHormonesMeasureG1Title,
    countKey: k.femaleHormonesMeasureG1Count,
    markersKey: k.femaleHormonesMeasureG1Markers,
    bodyKey: k.femaleHormonesMeasureG1Body,
    Icon: UterusOrganIcon,
    iconClassName: `h-16 w-16 ${GLYPH}`,
    tileImage: "/measure-tile-lipids.png",
  },
  {
    titleKey: k.femaleHormonesMeasureG3Title,
    countKey: k.femaleHormonesMeasureG3Count,
    markersKey: k.femaleHormonesMeasureG3Markers,
    bodyKey: k.femaleHormonesMeasureG3Body,
    Icon: MetabolismOrganIcon,
    iconClassName: `h-20 w-20 ${GLYPH}`,
    tileImage: "/measure-tile-liver.png",
  },
  {
    titleKey: k.femaleHormonesMeasureG4Title,
    countKey: k.femaleHormonesMeasureG4Count,
    markersKey: k.femaleHormonesMeasureG4Markers,
    bodyKey: k.femaleHormonesMeasureG4Body,
    Icon: ThyroidOrganIcon,
    iconClassName: `h-16 w-16 ${GLYPH}`,
    tileImage: "/measure-tile-thyroid.png",
  },
] as const

/* ── The rotating-reveal insight stories (§5 demonstration). Prose is i18n
   keys; the illustrative readings are inline data. Values sanity-checked in
   the stage-5 verification deck (plausible mid-cycle / follicular reads).
   The `structure` entry is the pelvic-MRI tab: it's dropped in markets where
   imaging isn't bookable (see HormoneReveal's `showImaging`). ── */
export type RevealTone = "green" | "sand" | "terra"

export const REVEAL_INSIGHTS = [
  {
    id: "pcos",
    titleKey: k.femaleHormonesReveal1Title,
    bodyKey: k.femaleHormonesReveal1Body,
    verdictKey: k.femaleHormonesReveal1Verdict,
    readings: [
      {
        markerKey: k.femaleHormonesMkTestosterone,
        value: "1.6",
        unit: "nmol/l",
        badgeKey: k.femaleHormonesBadgeNormal,
        tone: "green",
        zones: [
          { c: "sand", w: 22 },
          { c: "green", w: 53 },
          { c: "terra", w: 25 },
        ],
        dot: 58,
      },
      {
        markerKey: k.femaleHormonesMkShbg,
        value: "22",
        unit: "nmol/l",
        badgeKey: k.femaleHormonesBadgeLow,
        tone: "terra",
        zones: [
          { c: "terra", w: 30 },
          { c: "green", w: 52 },
          { c: "sand", w: 18 },
        ],
        dot: 15,
      },
    ],
  },
  {
    id: "cycle",
    titleKey: k.femaleHormonesReveal2Title,
    bodyKey: k.femaleHormonesReveal2Body,
    verdictKey: k.femaleHormonesReveal2Verdict,
    readings: [
      {
        markerKey: k.femaleHormonesMkProgesterone,
        value: "9",
        unit: "nmol/l",
        badgeKey: k.femaleHormonesBadgeLow,
        tone: "terra",
        zones: [
          { c: "terra", w: 34 },
          { c: "green", w: 50 },
          { c: "sand", w: 16 },
        ],
        dot: 14,
      },
      {
        markerKey: k.femaleHormonesMkEstradiol,
        value: "0.34",
        unit: "nmol/l",
        badgeKey: k.femaleHormonesBadgeNormal,
        tone: "green",
        zones: [
          { c: "sand", w: 22 },
          { c: "green", w: 54 },
          { c: "terra", w: 24 },
        ],
        dot: 50,
      },
    ],
  },
  {
    id: "controlLoop",
    titleKey: k.femaleHormonesReveal3Title,
    bodyKey: k.femaleHormonesReveal3Body,
    verdictKey: k.femaleHormonesReveal3Verdict,
    readings: [
      {
        markerKey: k.femaleHormonesMkFsh,
        value: "10.5",
        unit: "IU/l",
        badgeKey: k.femaleHormonesBadgeHighNormal,
        tone: "sand",
        zones: [
          { c: "green", w: 48 },
          { c: "sand", w: 30 },
          { c: "terra", w: 22 },
        ],
        dot: 58,
      },
      {
        markerKey: k.femaleHormonesMkLh,
        value: "6.2",
        unit: "IU/l",
        badgeKey: k.femaleHormonesBadgeNormal,
        tone: "green",
        zones: [
          { c: "sand", w: 20 },
          { c: "green", w: 55 },
          { c: "terra", w: 25 },
        ],
        dot: 44,
      },
    ],
  },
  {
    id: "structure",
    titleKey: k.femaleHormonesReveal4Title,
    bodyKey: k.femaleHormonesReveal4Body,
    verdictKey: k.femaleHormonesReveal4Verdict,
    readings: [],
  },
] as const

export type RevealInsight = (typeof REVEAL_INSIGHTS)[number]

/* The structure tab's three findings — the structural conditions a pelvic MRI
   flags directly, shown three-in-a-row (inverted for the white readout card)
   instead of the old pelvis locator. Reuses the §7b finding + severity copy so
   the two MRI beats stay in sync; `tone` maps to the report severity accents. */
export const REVEAL_FINDINGS = [
  {
    titleKey: k.femaleHormonesMriFinding1Title,
    badgeKey: k.femaleHormonesMriBadgeObservation,
    count: 1,
    tone: "green" as RevealTone,
  },
  {
    titleKey: k.femaleHormonesMriFinding2Title,
    badgeKey: k.femaleHormonesMriBadgeObservation,
    count: 2,
    tone: "green" as RevealTone,
  },
  {
    titleKey: k.femaleHormonesMriFinding3Title,
    badgeKey: k.femaleHormonesMriBadgeMinor,
    count: 1,
    tone: "sand" as RevealTone,
  },
] as const
